import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import {
    ApiRequestService,
    CardComponent,
    DatepickerComponent,
    DateUtilPipe,
    DateUtilService,
    DialogComponent,
    DialogConfiguration,
    DialogService,
    LoadingOverlayComponent,
    MAT_ICON,
    Material,
    PaginationComponent,
    SelectComponent,
    SelectOption,
    SessionPreferenceService,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService,
    Void
} from '@prekog/common-ui';
import { catchError, map, Observable, of, retry, switchMap } from 'rxjs';

import { FUTURE_DATE_ERROR, RECALCULATION_FAILED, SUCCESSFULLY_REMOVED } from '../shared/adminconstants';
import { Festival } from '../shared/festival';
import { MediaQueryService } from '../shared/media-query.service';
import { Partner } from '../shared/partner';
import { getProductEventTypeByValue, getProductEventTypeEnumKeys, ProductEventType } from '../shared/producteventtype.enum';
import { ReferenceDataService } from '../shared/reference-data.service';
import { getStorageTypeByValue, getStorageTypeDisplayValue, getStorageTypeEnumKeys, StorageType } from '../shared/storagetype.enum';
import { UtilService } from '../shared/util.service';
import { DeleteProductEventRequest } from './deleteproductevent.request';
import { GetProductEventsRequest } from './getproductevents.request';
import { GetProductEventsResponse } from './getproductevents.response';
import { ProductEvent } from './productevent';
import { ProductEventsFilter } from './producteventsfilter.model';
import { RecalculationService } from './recalculation.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-productevents',
    templateUrl: './productevents.component.html',
    styleUrls: ['./productevents.component.scss'],
    standalone: true,
    imports: [
        PaginationComponent,
        LoadingOverlayComponent,
        CardComponent,
        MatIconModule,
        DatepickerComponent,
        SelectComponent,
        ReactiveFormsModule,
        TranslationPipe,
        DateUtilPipe,
        DecimalPipe
    ]
})
export class ProductEventsComponent implements OnInit {
    defaultEndDate!: string;
    defaultStartDate!: string;
    deleteProductEventURI = '/admin/deleteProductEvent';
    displayEdit = false;
    endDate: Date = new Date();
    eventTypeKeys: (keyof typeof ProductEventType)[] = [];
    getProductEventsURI = '/admin/getProductEvents';
    isFiltersVisible = false;
    readonly isMobile = inject(MediaQueryService).createMatchSignal('(max-width: 768px)');
    readonly isMobileFilterOpen = signal(false);
    maxDate!: string;
    partnerOptions: SelectOption[] = [];
    partners: Partner[] = [];

    festivalOptions: SelectOption[] = [];
    festivals: Festival[] = [];

    productEvents: ProductEvent[] = [];
    productEventsFilter!: FormGroup;
    productEventType = ProductEventType;
    productEventTypeOptions: SelectOption[] = [];

    productOptions: SelectOption[] = [];
    products: Material[] = [];
    requestedPage = 0;
    requestedPageSize = 15;
    readonly shouldShowFilters = computed(() => {
        return !this.isMobile() || this.isMobileFilterOpen();
    });
    startDate: Date = new Date();
    fromStorageTypeOptions = signal<SelectOption[]>([]);
    toStorageTypeOptions = signal<SelectOption[]>([]);
    preorderOptions = signal<SelectOption[]>([]);
    reprintOptions = signal<SelectOption[]>([]);
    readonly savedProductEventTypes = signal<string[]>([]);
    readonly savedProductIds = signal<number[]>([]);
    readonly savedPartnerIds = signal<number[]>([]);
    readonly savedFestivalIds = signal<number[]>([]);
    readonly savedFromStorageType = signal<string | null>(null);
    readonly savedToStorageType = signal<string | null>(null);
    readonly savedPreorder = signal<string | null>(null);
    readonly savedReprint = signal<string | null>(null);
    readonly savedStartDate = signal<string>('');
    readonly savedEndDate = signal<string>('');
    storageType = StorageType;
    storageTypeKeys: (keyof typeof StorageType)[] = [];
    storeBaseUrl = inject<string>('STORE_BACKEND_URL' as never);
    totalItems = 100;
    private _sessionPreference = inject(SessionPreferenceService);

    private _fb = inject(FormBuilder);

    private _router = inject(Router);

    private _apiRequestService = inject(ApiRequestService);

    private _toastMessageService = inject(ToastmessageService);

    private _translateService = inject(TranslationService);

    private _dialogService = inject(DialogService);

    private _cdr = inject(ChangeDetectorRef);

    private _dateUtilService = inject(DateUtilService);

    private _referenceData = inject(ReferenceDataService);

    private _recalculationService = inject(RecalculationService);

    private _utilService = inject(UtilService);

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
        } else if (sessionStorage.getItem('token')) {
            this.eventTypeKeys = getProductEventTypeEnumKeys();
            this.storageTypeKeys = getStorageTypeEnumKeys();
            this._buildProductEventTypeOptions();
            this._buildStorageTypeOptions();
            this._buildPreorderOptions();
            this._buildReprintOptions();
            const dateDefaults = this._dateUtilService.getDefaultDateFilterValues('2023-01-01');
            this.defaultStartDate = dateDefaults.defaultStartDate;
            this.defaultEndDate = dateDefaults.defaultEndDate;
            this.startDate = new Date(dateDefaults.defaultStartDate);
            this.endDate = new Date(dateDefaults.defaultEndDate);
            this.maxDate = this._dateUtilService.getMaxDateForDateSelection(true);
            this.savedStartDate.set(this.defaultStartDate);
            this.savedEndDate.set(this.defaultEndDate);
            this._loadPartners();
            this._loadProducts();
            this._loadFestivals();
            this._setDefaultFilters();
            const savedFilters = this._sessionPreference.load<ProductEventsFilter>('productevents-filters');
            if (savedFilters) {
                this.productEventsFilter.patchValue(savedFilters);
                this.savedProductEventTypes.set(savedFilters.productEventTypes ?? []);
                this.savedProductIds.set(savedFilters.productIds ?? []);
                this.savedPartnerIds.set(savedFilters.partnerIds ?? []);
                this.savedFestivalIds.set(savedFilters.festivalIds ?? []);
                this.savedFromStorageType.set(savedFilters.fromStorageType ?? null);
                this.savedToStorageType.set(savedFilters.toStorageType ?? null);
                this.savedPreorder.set(
                    savedFilters.preorder !== null && savedFilters.preorder !== undefined ? String(savedFilters.preorder) : null
                );
                this.savedReprint.set(
                    savedFilters.reprint !== null && savedFilters.reprint !== undefined ? String(savedFilters.reprint) : null
                );
                this.savedStartDate.set(savedFilters.startDate ? String(savedFilters.startDate) : '');
                this.savedEndDate.set(savedFilters.endDate ? String(savedFilters.endDate) : '');
            }
            this.updateProductEventsByFilter();
        }
    }

    onRemoveClicked(removableIndex: number): void {

        const dialogConfiguration: DialogConfiguration = {
            messageKey: 'admin.dialog.confirmation.deleteProductEventMessage'
        };

        this._dialogService.openDialog(
            DialogComponent,
            dialogConfiguration,
            () => {
                this._onRemoveAnswer(this.productEvents[removableIndex], removableIndex);
            });
    }

    onEdit(editableIndex: number): void {
        const productEvent = this.productEvents[editableIndex];
        if (productEvent.productEventType === ProductEventType.SOLD_VIA_WEBSHOP) {
            return;
        }
        void this._router.navigate(['/editproductevent'], { state: { productEvent } });
    }

    isModifiable(productEvent: ProductEvent): boolean {
        return productEvent.productEventType !== ProductEventType.SOLD_VIA_WEBSHOP;
    }

    onPaginationChange(event: { page: number; size: number }): void {
        this.requestedPage = event.page;
        this.requestedPageSize = event.size;

        this._sessionPreference.save('productevents-filters', this.productEventsFilter.value);
        const getProductEventsRequest = this._createGetProductEventsRequest();
        this._loadProductEvents(getProductEventsRequest);
    }

    getStorageTypeDisplayValue(enumMember: StorageType | undefined): string | null {
        return getStorageTypeDisplayValue(enumMember);
    }

    getProductEventTypeByValue(searchValue: string | undefined): string {
        if (!searchValue) {
            return 'EmptyValue';
        }

        const result = getProductEventTypeByValue(searchValue);
        if (result) {
            return result;
        }

        return 'EmptyValue';
    }

    updateProductEventsByFilter(): void {
        this.requestedPage = 0;
        this._sessionPreference.save('productevents-filters', this.productEventsFilter.value);
        const getProductEventsRequest = this._createGetProductEventsRequest();

        this._loadProductEvents(getProductEventsRequest);
    }

    toggleMobileFilter(): void {
        this.isMobileFilterOpen.update((v) => !v);
    }

    onResetFilters(): void {
        this.requestedPage = 0;
        this.requestedPageSize = 15;
        this._sessionPreference.remove('productevents-filters');
        this._setDefaultFilters();
        this.savedProductEventTypes.set([]);
        this.savedProductIds.set([]);
        this.savedPartnerIds.set([]);
        this.savedFestivalIds.set([]);
        this.savedFromStorageType.set(null);
        this.savedToStorageType.set(null);
        this.savedPreorder.set(null);
        this.savedReprint.set(null);
        this.savedStartDate.set(this.defaultStartDate);
        this.savedEndDate.set(this.defaultEndDate);
        this.updateProductEventsByFilter();
    }

    onProductEventTypesChange(selectedIds: (number | string)[]): void {
        this.productEventsFilter.patchValue({ productEventTypes: selectedIds });
    }

    onProductIdsChange(selectedIds: (number | string)[]): void {
        this.productEventsFilter.patchValue({ productIds: selectedIds });
    }

    onPartnerIdsChange(selectedIds: (number | string)[]): void {
        this.productEventsFilter.patchValue({ partnerIds: selectedIds });
    }

    onFestivalIdsChange(selectedIds: (number | string)[]): void {
        this.productEventsFilter.patchValue({ festivalIds: selectedIds });
    }

    onFromStorageTypeSelect(id: number | string | null): void {
        this.productEventsFilter.patchValue({ fromStorageType: id });
    }

    onToStorageTypeSelect(id: number | string | null): void {
        this.productEventsFilter.patchValue({ toStorageType: id });
    }

    onPreorderSelect(id: number | string | null): void {
        const value = id === 'true' ? true : id === 'false' ? false : null;
        this.productEventsFilter.patchValue({ preorder: value });
    }

    onReprintSelect(id: number | string | null): void {
        const value = id === 'true' ? true : id === 'false' ? false : null;
        this.productEventsFilter.patchValue({ reprint: value });
    }

    onStartDateChange(date: string): void {
        this.productEventsFilter.patchValue({ startDate: date });
    }

    onEndDateChange(date: string): void {
        this.productEventsFilter.patchValue({ endDate: date });
    }

    private _showFutureDateError(): void {
        this._toastMessageService.displayToastMessage(
            TOAST_STATE.error,
            this._translateService.translate(FUTURE_DATE_ERROR),
            MAT_ICON.error
        );
    }

    private _setDefaultFilters(): void {
        const futureDateValidator = this._dateUtilService.futureDateValidator(() => this._showFutureDateError());

        this.productEventsFilter = this._fb.group({
            productEventTypes: [[]],
            fromStorageType: [null],
            toStorageType: [null],
            productIds: [[]],
            partnerIds: [[]],
            festivalIds: [[]],
            preorder: [null],
            reprint: [null],
            startDate: [this.defaultStartDate, [Validators.required, futureDateValidator]],
            endDate: [this.defaultEndDate, [Validators.required, futureDateValidator]]
        });
    }

    private _onRemoveAnswer(productEventForDelete: ProductEvent, removableIndex: number): void {
        const deleteProductEventRequest = this._mapProductEventForDeleteToDeletePorductEventRequest(productEventForDelete);

        this._apiRequestService
            .sendRequest<DeleteProductEventRequest, Void>(this.storeBaseUrl, deleteProductEventRequest, true, this.deleteProductEventURI)
            .pipe(switchMap(() => this._executeDeleteRecalculation(productEventForDelete)))
            .subscribe({
                next: (recalculationSucceeded) => {
                    this._toastMessageService.displayToastMessage(
                        TOAST_STATE.success,
                        this._translateService.translate(SUCCESSFULLY_REMOVED),
                        MAT_ICON.success
                    );
                    this.productEvents.splice(removableIndex, 1);
                    if (!recalculationSucceeded) {
                        this._toastMessageService.displayToastMessage(
                            TOAST_STATE.error,
                            this._translateService.translate(RECALCULATION_FAILED),
                            MAT_ICON.error
                        );
                    }
                    this.updateProductEventsByFilter();
                }
            });
    }

    private _executeDeleteRecalculation(productEvent: ProductEvent): Observable<boolean> {
        if (productEvent.productId === undefined || productEvent.productId === null || !productEvent.eventDate) {
            return of(true);
        }
        const dateStr = this._formatEventDate(productEvent.eventDate);
        if (!dateStr) {
            return of(true);
        }
        const request = this._recalculationService.buildDeleteRecalculationRequest(
            productEvent.productId, dateStr
        );
        if (!request) {
            return of(true);
        }
        return this._recalculationService.sendRecalculationRequest(request).pipe(
            retry(2),
            map(() => true),
            catchError(() => of(false))
        );
    }

    private _formatEventDate(eventDate: unknown): string | null {
        if (Array.isArray(eventDate) && eventDate.length === 3) {
            const [year, month, day] = eventDate;
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
        if (typeof eventDate === 'string') {
            return eventDate;
        }
        return null;
    }

    private _mapProductEventForDeleteToDeletePorductEventRequest(productEventForDelete: ProductEvent): DeleteProductEventRequest {
        const deleteProductEventRequest: DeleteProductEventRequest = {
            comment: productEventForDelete?.comment,

            eventDate: productEventForDelete?.eventDate,

            id: productEventForDelete?.id,

            referenceNumber: productEventForDelete?.referenceNumber,

            partnerId: productEventForDelete?.partnerId,

            unitPrice: productEventForDelete?.unitPrice,

            productEventType: productEventForDelete?.productEventType,

            productId: productEventForDelete?.productId,

            quantity: productEventForDelete?.quantity
        };

        return deleteProductEventRequest;
    }

    private _loadProducts(): void {
        this._referenceData.loadProducts().subscribe((products) => {
            this.products = products;
            this.productOptions = this.products.map((p) => ({
                id: p.id!,
                name: `${p.title} - ${p.author} [${p.isbn}]`
            }));
            this._cdr.markForCheck();
        });
    }

    private _loadPartners(): void {
        this._referenceData.loadPartners().subscribe((partners) => {
            this.partners = partners;
            this.partnerOptions = this.partners.map((p) => ({
                id: p.id!,
                name: p.name!
            }));
            this._cdr.markForCheck();
        });
    }

    private _loadFestivals(): void {
        this._referenceData.loadFestivals().subscribe((festivals) => {
            this.festivals = festivals;
            this.festivalOptions = this.festivals.map((f) => ({
                id: f.id!,
                name: f.name!
            }));
            this._cdr.markForCheck();
        });
    }

    private _createGetProductEventsRequest(): GetProductEventsRequest {
        const filters = this.productEventsFilter.value as ProductEventsFilter;

        return {
            page: this.requestedPage,
            size: this.requestedPageSize,
            partnerIds: this._utilService.nullIfEmpty(filters.partnerIds),
            productIds: this._utilService.nullIfEmpty(filters.productIds),
            festivalIds: this._utilService.nullIfEmpty(filters.festivalIds),
            eventTypes: this._utilService.nullIfEmpty(filters.productEventTypes),
            fromStorageType: getStorageTypeByValue(filters.fromStorageType),
            toStorageType: getStorageTypeByValue(filters.toStorageType),
            preorder: filters.preorder,
            reprint: filters.reprint,
            fromDate: filters.startDate,
            toDate: filters.endDate
        };
    }

    private _buildProductEventTypeOptions(): void {
        this.productEventTypeOptions = this.eventTypeKeys.map((key) => ({
            id: this.productEventType[key],
            name: this._translateService.translate('admin.product.event.type.' + key)
        }));
    }

    private _buildStorageTypeOptions(): void {
        const options = this.storageTypeKeys.map((key) => ({
            id: this.storageType[key],
            name: this._translateService.translate('admin.product.statistics.storage.' + this.storageType[key])
        }));
        this.fromStorageTypeOptions.set(options);
        this.toStorageTypeOptions.set(options);
    }

    private _buildPreorderOptions(): void {
        this.preorderOptions.set([
            { id: 'false', name: this._translateService.translate('admin.product.event.field.order') },
            { id: 'true', name: this._translateService.translate('admin.product.event.field.preorder') }
        ]);
    }

    private _buildReprintOptions(): void {
        this.reprintOptions.set([
            { id: 'false', name: this._translateService.translate('admin.product.event.field.firstPrint') },
            { id: 'true', name: this._translateService.translate('admin.product.event.field.reprint') }
        ]);
    }

    private _loadProductEvents(getProductEventsRequest: GetProductEventsRequest): void {
        this._apiRequestService
            .sendRequest(this.storeBaseUrl, getProductEventsRequest, true, this.getProductEventsURI)
            .subscribe((response) => {
                const productEvents = response as GetProductEventsResponse;
                this.productEvents = productEvents.productEventList as ProductEvent[];
                this.totalItems = productEvents.totalItems as number;
                this._cdr.markForCheck();
            });
    }
}
